// src/components/admin/CenterCodes.jsx
import React, { useState, useEffect } from 'react';
import { Key, Plus, Trash2, Copy, RefreshCw, CheckCircle, User, Users } from 'lucide-react';
import * as d1Api from '../../services/d1Api';

export const CenterCodes = ({ courses = [] }) => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);

  // Code type: 'open' (group) or 'student' (specific student)
  const [codeType, setCodeType] = useState('open');

  const [newCodeForm, setNewCodeForm] = useState({
    course_id: '',
    max_uses: 5,
    expires_at: '',       // FIX: was 'expiryDate' — now aligned to backend column name
    student_name: '',
    student_phone: '',
  });

  useEffect(() => {
    loadCodes();
  }, []);

  const loadCodes = async () => {
    setLoading(true);
    const res = await d1Api.getCenterCodes();
    if (res.success) setCodes(res.data || []);
    setLoading(false);
  };

  const generateRandomCode = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let code = 'C';
    for (let i = 0; i < 9; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  };

  const handleCreate = async () => {
    if (!newCodeForm.course_id) {
      alert('الرجاء اختيار الكورس أولاً');
      return;
    }

    if (codeType === 'student' && !newCodeForm.student_name.trim()) {
      alert('الرجاء إدخال اسم الطالب');
      return;
    }

    setGenerating(true);
    const code = generateRandomCode();

    // FIX: Send expires_at (ISO date string) — the worker converts it to a Unix timestamp.
    // For student codes, usageLimit is always 1.
    const payload = {
      code,
      course_id: newCodeForm.course_id,
      usageLimit: codeType === 'student' ? 1 : Number(newCodeForm.max_uses) || 1,
      // Only include expires_at if the admin actually selected a date
      ...(newCodeForm.expires_at && { expires_at: newCodeForm.expires_at }),
      ...(codeType === 'student' && {
        student_name: newCodeForm.student_name.trim(),
        student_phone: newCodeForm.student_phone.trim(),
      }),
    };

    const res = await d1Api.createCenterCode(payload);
    if (res.success) {
      await loadCodes();
      setNewCodeForm({ course_id: '', max_uses: 5, expires_at: '', student_name: '', student_phone: '' });
    } else {
      alert('حدث خطأ: ' + (res.error || 'لم يتم إنشاء الكود'));
    }
    setGenerating(false);
  };

  const handleDelete = async (codeId) => {
    if (!confirm('هل أنت متأكد من حذف هذا الكود؟')) return;
    const res = await d1Api.deleteCenterCode(codeId);
    if (res.success) loadCodes();
    else alert('حدث خطأ: ' + (res.error || 'لم يتم الحذف'));
  };

  const handleCopy = (code) => {
    navigator.clipboard.writeText(code).then(() => {
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <Key size={24} />
          أكواد السنتر
        </h2>
        <button
          onClick={loadCodes}
          className="bg-white/10 hover:bg-white/20 px-4 py-2 rounded-xl font-bold transition flex items-center gap-2"
        >
          <RefreshCw size={16} />
          تحديث
        </button>
      </div>

      {/* Create Code Form */}
      <div className="glass-panel p-6">
        <h3 className="font-bold mb-4 flex items-center gap-2">
          <Plus size={18} />
          إنشاء كود جديد
        </h3>

        {/* Code Type Toggle */}
        <div className="flex gap-3 mb-5">
          <button
            onClick={() => setCodeType('open')}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-bold transition border-2 ${
              codeType === 'open'
                ? 'bg-blue-500/20 border-blue-500/50 text-blue-300'
                : 'bg-white/5 border-white/10 text-slate-400 hover:bg-white/10'
            }`}
          >
            <Users size={18} />
            كود مفتوح (مجموعة طلاب)
          </button>
          <button
            onClick={() => setCodeType('student')}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-bold transition border-2 ${
              codeType === 'student'
                ? 'bg-purple-500/20 border-purple-500/50 text-purple-300'
                : 'bg-white/5 border-white/10 text-slate-400 hover:bg-white/10'
            }`}
          >
            <User size={18} />
            كود مخصص لطالب محدد
          </button>
        </div>

        <div className="grid md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm text-slate-400 mb-1">الكورس</label>
            <select
              value={newCodeForm.course_id}
              onChange={(e) => setNewCodeForm({ ...newCodeForm, course_id: e.target.value })}
              className="w-full p-3 bg-white/5 border border-white/10 rounded-xl text-white"
            >
              <option value="">-- اختر الكورس --</option>
              {courses.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.title}
                </option>
              ))}
            </select>
          </div>

          {codeType === 'open' ? (
            <div>
              <label className="block text-sm text-slate-400 mb-1">عدد الاستخدامات</label>
              <input
                type="number"
                min="1"
                value={newCodeForm.max_uses}
                onChange={(e) => setNewCodeForm({ ...newCodeForm, max_uses: e.target.value })}
                className="w-full p-3 bg-white/5 border border-white/10 rounded-xl text-white"
              />
            </div>
          ) : (
            <>
              <div>
                <label className="block text-sm text-slate-400 mb-1">اسم الطالب</label>
                <input
                  type="text"
                  placeholder="أدخل اسم الطالب"
                  value={newCodeForm.student_name}
                  onChange={(e) => setNewCodeForm({ ...newCodeForm, student_name: e.target.value })}
                  className="w-full p-3 bg-white/5 border border-white/10 rounded-xl text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1">هاتف الطالب (اختياري)</label>
                <input
                  type="text"
                  placeholder="01xxxxxxxxx"
                  value={newCodeForm.student_phone}
                  onChange={(e) => setNewCodeForm({ ...newCodeForm, student_phone: e.target.value })}
                  className="w-full p-3 bg-white/5 border border-white/10 rounded-xl text-white"
                />
              </div>
            </>
          )}

          {/* FIX: Field key is expires_at (was expires_at in state but was labelled expiryDate
              in the old payload). Now consistently expires_at end-to-end. */}
          <div>
            <label className="block text-sm text-slate-400 mb-1">تاريخ الإنتهاء (اختياري)</label>
            <input
              type="date"
              value={newCodeForm.expires_at}
              onChange={(e) => setNewCodeForm({ ...newCodeForm, expires_at: e.target.value })}
              className="w-full p-3 bg-white/5 border border-white/10 rounded-xl text-white"
            />
          </div>
        </div>

        {codeType === 'student' && (
          <p className="text-xs text-purple-400 mb-3">⚠️ كود الطالب المخصص يُستخدم مرة واحدة فقط</p>
        )}

        <button
          onClick={handleCreate}
          disabled={generating}
          className="btn-main px-6 py-3 rounded-xl font-bold flex items-center gap-2 disabled:opacity-50"
        >
          <Plus size={18} />
          {generating ? 'جاري الإنشاء...' : 'إنشاء كود'}
        </button>
      </div>

      {/* Codes List */}
      {loading ? (
        <div className="glass-panel p-12 text-center">
          <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-400">جاري التحميل...</p>
        </div>
      ) : codes.length === 0 ? (
        <div className="glass-panel p-12 text-center">
          <Key size={48} className="mx-auto mb-4 opacity-30" />
          <h3 className="text-xl font-bold mb-2">لا توجد أكواد</h3>
          <p className="text-slate-400">أنشئ كود أعلاه لمنح وصول للطلاب</p>
        </div>
      ) : (
        <div className="glass-panel overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-right">
              <thead className="border-b border-white/10 bg-white/5">
                <tr>
                  <th className="p-4 text-slate-400">الكود</th>
                  <th className="p-4 text-slate-400">الكورس</th>
                  <th className="p-4 text-slate-400">النوع</th>
                  <th className="p-4 text-slate-400">الاستخدامات</th>
                  <th className="p-4 text-slate-400">تاريخ الإنتهاء</th>
                  <th className="p-4 text-slate-400 text-center">إجراءات</th>
                </tr>
              </thead>
              <tbody>
                {codes.map((c) => {
                  // FIX: Resolve expiry from expires_at with fallback to legacy expiryDate
                  const expiryTs = c.expires_at || c.expiryDate || null;
                  return (
                    <tr key={c.id} className="border-b border-white/5 hover:bg-white/5 transition">
                      <td className="p-4">
                        <span className="font-mono text-lg font-bold tracking-widest text-brand-accent">
                          {c.code}
                        </span>
                      </td>
                      <td className="p-4 text-slate-300">
                        {courses.find((co) => co.id === c.course_id)?.title || c.course_id || '-'}
                      </td>
                      <td className="p-4">
                        {c.student_name ? (
                          <span className="text-purple-400 text-xs font-bold">
                            <User size={12} className="inline mr-1" />
                            {c.student_name}
                          </span>
                        ) : (
                          <span className="text-blue-400 text-xs font-bold">
                            <Users size={12} className="inline mr-1" />
                            مفتوح
                          </span>
                        )}
                      </td>
                      <td className="p-4">
                        <span className="text-slate-300">
                          {c.currentUsage ?? 0} / {c.usageLimit ?? 1}
                        </span>
                      </td>
                      {/* FIX: Display expiry from the unified expires_at field */}
                      <td className="p-4 text-slate-400 text-sm">
                        {expiryTs
                          ? new Date(Number(expiryTs)).toLocaleDateString('ar-EG')
                          : 'بلا تاريخ'}
                      </td>
                      <td className="p-4 text-center">
                        <div className="flex gap-2 justify-center">
                          <button
                            onClick={() => handleCopy(c.code)}
                            className="bg-blue-500/20 text-blue-400 hover:bg-blue-500 hover:text-white p-2 rounded-lg transition"
                            title="نسخ الكود"
                          >
                            {copiedCode === c.code ? (
                              <CheckCircle size={16} />
                            ) : (
                              <Copy size={16} />
                            )}
                          </button>
                          <button
                            onClick={() => handleDelete(c.id)}
                            className="bg-red-500/20 text-red-400 hover:bg-red-500 hover:text-white p-2 rounded-lg transition"
                            title="حذف الكود"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
